import React, { useState, useRef, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import TurnstileWidget, { isTurnstileConfigured } from "components/Auth/TurnstileWidget";

function Contact() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // SOUND-722. The honeypot alone was beaten in four seconds: the 2026-08-27
  // bot submitted once with the hidden field filled, was silently suppressed,
  // then resubmitted with only the visible fields and reached the inbox.
  const turnstileTokenRef = useRef(null);
  const turnstileWidgetRef = useRef(null);
  const handleTurnstileToken = useCallback((token) => {
    turnstileTokenRef.current = token;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get("name");
    const email = formData.get("email");
    const company = formData.get("company");
    const message = formData.get("message");
    // SOUND-714 honeypot. Hidden from people, irresistible to form bots. Sent
    // as-is; the API decides. A blank value is the normal case.
    const website = formData.get("website");

    setLoading(true);

    try {
      const response = await makeApiRequest(
        "post",
        apiRoutes.contactUs,
        null,
        null,
        { name, email, company, message, website, turnstileToken: turnstileTokenRef.current }
      );

      if (response.status === 200) {
        setSubmitted(true);
        e.target.reset();
      } else {
        window.alert(
          "There was an issue submitting your message. Please try again."
        );
      }
    } catch (error) {
      console.error(error);
      // A spent or missing token: single-use, so the one in hand is dead
      // whatever the reason. Reset or the retry fails identically forever.
      if (error?.response?.data?.code === "CHALLENGE_FAILED") {
        turnstileWidgetRef.current?.reset();
      }
      // axios REJECTS on 4xx, so a validation 400 lands here, not in the
      // `else` above. Surface what the API said — SOUND-714 returns a real
      // reason ("Email is not a valid address.") and swallowing it leaves the
      // person re-submitting the same broken value.
      window.alert(
        error?.response?.data?.error ||
          "There was an error submitting the form. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section
        id="contact"
        className="bg-background text-foreground font-jakarta font-light px-6 md:px-[75px] text-left"
      >
        <h1 className="text-4xl md:text-5xl leading-tight mb-4">
          <br />
          Get In Touch<span className="text-brand">.</span>
        </h1>
        <h2 className="font-normal text-lg md:text-xl max-w-3xl mb-8">
          Looking to optimize your payments strategy? Let us know what you need
          help with, and we’ll get back to you soon. If you’d rather jump right
          into a conversation, schedule a discovery call using the link below.
        </h2>

        {submitted ? (
          <div className="text-green-400 text-lg">
            Thanks! We'll be in touch soon.
          </div>
        ) : (
          <form className="space-y-6 max-w-lg" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="name" className="block mb-1 text-sm">
                Name
              </label>
              <input
                type="text"
                name="name"
                required
                className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div>
              <label htmlFor="email" className="block mb-1 text-sm">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div>
              <label htmlFor="company" className="block mb-1 text-sm">
                Company
              </label>
              <input
                type="text"
                name="company"
                className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            {/*
              SOUND-714 honeypot. NOT `display:none` — a bot that parses styles
              skips those, and so do some password managers. Positioned off-canvas
              instead, with tabIndex -1 and aria-hidden so keyboard and screen
              reader users never reach it. A human cannot fill this in; a naive
              bot fills every input it finds.
            */}
            <div className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden">
              <label htmlFor="contact-website" aria-hidden="true">
                Do not fill this in
              </label>
              <input
                type="text"
                id="contact-website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
            </div>

            <div>
              <label htmlFor="message" className="block mb-1 text-sm">
                Message
              </label>
              <textarea
                name="message"
                rows="5"
                required
                className="w-full px-4 py-2 rounded-md bg-card border border-border focus:outline-none focus:ring-2 focus:ring-brand resize-none"
              />
            </div>

            {isTurnstileConfigured() && (
              <TurnstileWidget
                ref={turnstileWidgetRef}
                onToken={handleTurnstileToken}
                className="mt-4"
              />
            )}

            <div className="flex items-center gap-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                className="inline-block px-6 py-3 bg-brand text-background text-sm font-semibold rounded-lg hover:bg-brand-400 transition disabled:opacity-50"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

export default Contact;
